import type { FormikErrors } from 'formik';
import { Form, Formik } from 'formik';
import { useState } from 'react';

import type { VesselMessage } from '../../types/VesselMessage';
import { Button } from '../cdl/Button/Button';
import { DrawerContentLayout } from '../cdl/Drawer/DrawerContentLayout';
import { FormikDebug } from '../common/form-elements/formik/FormikDebug';
import { translate } from '../common/helpers/translate.helper';
import { FormikVesselSelect } from '../vessel/FormikVesselSelect';

interface VesselLimitationFormValues {
    vesselId: string | null;
}

export interface VesselLimitationSubmitArgs {
    data: VesselMessage;
    oldVessel?: VesselMessage;
}

interface VesselLimitationFormProps {
    data?: { vessel: VesselMessage };
    onSubmit: (args: VesselLimitationSubmitArgs) => void;
    groupId: string | null;
    primaryActionText: string;
}

export const VesselLimitationForm = ({
    data: oldLimitation,
    onSubmit,
    groupId,
    primaryActionText,
}: VesselLimitationFormProps) => {
    const [selectedVessel, setSelectedVessel] = useState<VesselMessage | null>(oldLimitation?.vessel ?? null);
    const initialValues: VesselLimitationFormValues = {
        vesselId: oldLimitation?.vessel.id ?? null,
    };

    const validate = (values: VesselLimitationFormValues) => {
        const errors: FormikErrors<VesselLimitationFormValues> = {};

        if (!values.vesselId) {
            errors.vesselId = translate('errormessage.empty');
        }

        return errors;
    };

    const handleSubmit = () => {
        if (!selectedVessel) {
            return;
        }

        onSubmit({
            data: selectedVessel,
            oldVessel: oldLimitation?.vessel,
        });
    };

    return (
        <Formik initialValues={initialValues} validate={validate} validateOnMount onSubmit={handleSubmit}>
            {({ isSubmitting, dirty }) => (
                <Form style={{ height: '100%', minHeight: 0 }}>
                    <DrawerContentLayout
                        isBusy={isSubmitting}
                        hasUnsavedChanges={dirty}
                        primaryAction={
                            <Button type="submit" emphasis="high" isLoading={isSubmitting}>
                                {primaryActionText}
                            </Button>
                        }
                    >
                        <FormikVesselSelect
                            name="vesselId"
                            groupId={groupId ?? undefined}
                            onVesselSelect={setSelectedVessel}
                            isClearable
                            markAsRequired
                        />

                        <FormikDebug />
                    </DrawerContentLayout>
                </Form>
            )}
        </Formik>
    );
};
